import type { CountryCode } from "libphonenumber-js";
import React from "react";
import styled, { css } from "styled-components";

import type { countries } from "../../../../shared/assets/countries";
import type { configurationManager } from "../../../../shared/core/service/services";
import type { Account } from "../../../../shared/domains/account/account";
import type { Recipient } from "../../../../shared/domains/recipients/recipient";
import type { AccountOrRecipient } from "../../../../shared/domains/transactions/customer-instruction";
import { flagUrl } from "../../../utils/flag-url";
import type { SourceAndDestinationEnum } from "../../screen/transfer/components/source-and-destination";
import FormLabel from "../forms/form-label";
import type { PrefixedSelectInputType } from "../forms/prefixed-select-input";
import { PrefixedSelectInput } from "../forms/prefixed-select-input";
import { PrefixContainer, PrefixedTextInput } from "../forms/prefixed-text-input";
import { TextInput } from "../forms/text-input";
import { SourceAccountSelection } from "./source-account-selection";

interface P2PSimpleTransferProps {
  accounts: Account[];
  sourceAccount: AccountOrRecipient | null;
  onOpenSourceAccountSelection?: () => void;
  editingSourceAndDestination?: SourceAndDestinationEnum | null;
  formatMessage: (id: string) => string;
  recipient?: Recipient;
  recipientData: Partial<Recipient> | null;
  setRecipientData: React.Dispatch<React.SetStateAction<Partial<Recipient> | null>>;
  countryCode: CountryCode;
  setCountryCode: React.Dispatch<React.SetStateAction<CountryCode>>;
  configuration: typeof configurationManager.configuration;
  countries: typeof countries;
  hasError: boolean;
  setDataError: React.Dispatch<React.SetStateAction<string | undefined>>;
  editPhone: (phoneNumber: string) => void;
}

export const P2PSimpleTransfer: React.FC<P2PSimpleTransferProps> = ({
  accounts,
  sourceAccount,
  onOpenSourceAccountSelection,
  editingSourceAndDestination,
  formatMessage,
  recipient,
  recipientData,
  setRecipientData,
  countryCode,
  setCountryCode,
  configuration,
  countries,
  hasError,
  setDataError,
  editPhone,
}) => {
  const recipientName = recipient?.name ?? recipientData?.name;
  const recipientPhone = recipient?.phone ?? recipientData?.phone;
  const selectedCountry = countries[countryCode];

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRecipientData({ ...recipientData, name: event.target.value });
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (hasError) {
      setDataError(undefined);
    }
    editPhone(event.target.value);
  };

  return (
    <>
      {accounts.length > 1 && (
        <SourceAccountSelection
          sourceAccount={sourceAccount}
          onOpenSourceAccountSelection={onOpenSourceAccountSelection}
          editingSourceAndDestination={editingSourceAndDestination}
          formatMessage={formatMessage}
        />
      )}
      <FormLabel size="normal" margin="normal" label="amountSelection.simpleTransferNameLabel" />
      <TextInput
        value={recipientName}
        onChange={handleNameChange}
        placeholder={formatMessage("amountSelection.simpleTransferNamePlaceholder")}
      />
      <FormLabel size="normal" margin="normal" label="amountSelection.simpleTransferPhoneLabel" />
      <CountrySelectInput
        onChange={setCountryCode}
        innerId="country-select-field"
        options={configuration.countries}
        itemRenderer={(code) => countries[code].englishName}
        value={countryCode}
        prefixElement={selectedCountry && <FlagIcon src={flagUrl(countryCode)} alt="" />}
      />
      <PhoneInput
        placeholder={formatMessage("amountSelection.simpleTransferPhonePlaceholder")}
        prefixElement={<PhoneCode>+{selectedCountry?.phoneCode}</PhoneCode>}
        type="tel"
        value={recipientPhone}
        onChange={handlePhoneChange}
        hasError={hasError}
        errorMessage={formatMessage("common.invalidPhoneNumber")}
      />
    </>
  );
};

const FlagIcon = styled.img`
  width: 14px;
  height: 14px;
  border-radius: 7px;
`;

const PhoneInput = styled(PrefixedTextInput)`
  ${PrefixContainer} {
    width: 80px;
  }
`;

const PhoneCode = styled.span`
  font-size: 0.875rem;
`;

const inputStyle = css`
  width: 100%;
  margin-bottom: 25px;
`;

const CountrySelectInput = styled<PrefixedSelectInputType<CountryCode>>(PrefixedSelectInput)`
  ${inputStyle};
`;
